/**
 * Admin → Waiting Confirmation.
 *
 * Lists customers whose status is not yet 'active' (pending_admin_review,
 * pending_customer_confirmation, revisions_requested). The admin reviews the
 * full registration profile, optionally adds internal notes, then clicks
 * "Finalize" to push the customer to the next stage (customer must then
 * confirm in their cabinet to become 'active').
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Eye,
  Mail,
  Phone,
  Crown,
  Clock,
  CheckCircle2,
  StickyNote,
  RotateCcw,
  PawPrint,
  MapPin,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

const STATUS_META = {
  pending_admin_review: {
    label: 'Awaiting your review',
    bg: 'bg-amber-100 text-amber-800',
  },
  pending_customer_confirmation: {
    label: 'Awaiting customer',
    bg: 'bg-blue-100 text-blue-800',
  },
  revisions_requested: {
    label: 'Revisions requested',
    bg: 'bg-rose-100 text-rose-800',
  },
};

export default function AdminPendingCustomers() {
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [detailUser, setDetailUser] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [reverting, setReverting] = useState(false);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const data = await adminApi.listPendingCustomers();
      setRows(data || []);
    } catch (e) {
      setLoadError(e.message || 'Failed to load pending customers');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!detailUser?.id) return;
    let alive = true;
    setDetails(null);
    setDetailsLoading(true);
    (async () => {
      try {
        const d = await adminApi.getCabinetCustomerDetails(detailUser.id);
        if (alive) {
          setDetails(d);
          setNotesDraft(d?.user?.admin_notes || '');
        }
      } catch (e) {
        if (alive) toast.error(e.message || 'Failed to load profile');
      } finally {
        if (alive) setDetailsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [detailUser?.id]);

  const buckets = useMemo(() => {
    const out = {
      pending_admin_review: [],
      pending_customer_confirmation: [],
      revisions_requested: [],
    };
    (rows || []).forEach((r) => {
      const s = r.customer_status || 'pending_admin_review';
      if (out[s]) out[s].push(r);
    });
    return out;
  }, [rows]);

  const closeDrawer = () => {
    setDetailUser(null);
    setDetails(null);
    setNotesDraft('');
  };

  const onSaveNotes = async () => {
    if (!detailUser?.id) return;
    setSavingNotes(true);
    try {
      await adminApi.setAdminNotes(detailUser.id, notesDraft || ' ');
      toast.success('Notes saved');
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSavingNotes(false);
    }
  };

  const onFinalize = async () => {
    if (!detailUser?.id) return;
    setFinalizing(true);
    try {
      // Persist any unsaved notes first
      if (notesDraft !== (details?.user?.admin_notes || '')) {
        await adminApi.setAdminNotes(detailUser.id, notesDraft || ' ');
      }
      await adminApi.finalizeCustomer(detailUser.id, notesDraft);
      toast.success('Finalized — customer notified to confirm');
      closeDrawer();
      await load();
    } catch (e) {
      toast.error(`Finalize failed: ${e.message}`);
    } finally {
      setFinalizing(false);
    }
  };

  const onRevert = async () => {
    if (!detailUser?.id) return;
    setReverting(true);
    try {
      await adminApi.revertToReview(detailUser.id);
      toast.success('Sent back to review');
      closeDrawer();
      await load();
    } catch (e) {
      toast.error(`Revert failed: ${e.message}`);
    } finally {
      setReverting(false);
    }
  };

  const renderRow = (r) => {
    const meta = STATUS_META[r.customer_status] || STATUS_META.pending_admin_review;
    return (
      <tr
        key={r.id}
        className="border-b border-[#0A4D8C14] last:border-0 hover:bg-[#FDFBF7]"
        data-testid={`pending-row-${r.id}`}
      >
        <td className="px-3 py-3">
          <button
            type="button"
            onClick={() => setDetailUser(r)}
            data-testid={`view-pending-${r.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0A4D8C] hover:bg-[#053B70] text-white text-[11px] font-bold px-3 py-1.5"
          >
            <Eye size={12} /> Review
          </button>
        </td>
        <td className="px-3 py-3">
          <div className="font-semibold text-[#05223D]">{r.name || '—'}</div>
          {r.founder_no && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#F25C0526] text-[#F25C05] text-[10px] font-bold px-2 py-0.5 mt-1">
              <Crown size={10} /> #{r.founder_no}
            </span>
          )}
        </td>
        <td className="px-3 py-3 text-sm text-[#05223D]">
          <div className="flex items-center gap-1 text-[#465B70]">
            <Mail size={11} /> {r.email}
          </div>
          {r.phone && (
            <div className="flex items-center gap-1 text-[#0A4D8C] font-semibold mt-0.5">
              <Phone size={11} /> {r.phone}
            </div>
          )}
        </td>
        <td className="px-3 py-3 text-xs text-[#465B70]">
          <span title="Pets">🐾 {r.pet_count ?? 0}</span>{' '}·{' '}
          <span title="Addresses">📍 {r.address_count ?? 0}</span>{' '}·{' '}
          <span title="Subs">🔁 {r.subscription_count ?? 0}</span>
        </td>
        <td className="px-3 py-3">
          <span
            className={`inline-block rounded-full text-[10px] font-bold px-2 py-1 ${meta.bg}`}
          >
            {meta.label}
          </span>
        </td>
        <td className="px-3 py-3 text-xs text-[#7A8A99] tabular-nums">
          {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-pending-customers-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[.18em] text-[#F25C05] font-bold">
            Registration pipeline
          </p>
          <h1 className="font-display text-3xl text-[#05223D]">
            Waiting Confirmation
          </h1>
          <p className="text-sm text-[#465B70] mt-1">
            Review newly registered customers, fill in prices &amp; products, then finalize
            so they can confirm and become active.
          </p>
        </div>
      </header>

      {loadError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {loadError}
        </div>
      )}

      {!rows ? (
        <div className="flex items-center gap-2 text-[#465B70]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      ) : (
        <>
          {/* Headline stats */}
          <div className="grid grid-cols-3 gap-3" data-testid="pending-stats-strip">
            <StatCard
              label="Awaiting your review"
              count={buckets.pending_admin_review.length}
              icon={<Clock className="h-4 w-4 text-amber-700" />}
              tone="amber"
            />
            <StatCard
              label="Awaiting customer"
              count={buckets.pending_customer_confirmation.length}
              icon={<CheckCircle2 className="h-4 w-4 text-blue-700" />}
              tone="blue"
            />
            <StatCard
              label="Revisions requested"
              count={buckets.revisions_requested.length}
              icon={<AlertCircle className="h-4 w-4 text-rose-700" />}
              tone="rose"
            />
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-[#0A4D8C1A] bg-white overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-[#F5F2EB] text-[10px] uppercase tracking-wider text-[#465B70]">
                <tr>
                  <th className="px-3 py-2 font-bold"></th>
                  <th className="px-3 py-2 font-bold">Customer</th>
                  <th className="px-3 py-2 font-bold">Contact</th>
                  <th className="px-3 py-2 font-bold">Profile</th>
                  <th className="px-3 py-2 font-bold">Status</th>
                  <th className="px-3 py-2 font-bold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-sm text-[#7A8A99]"
                    >
                      No pending customers. New signups will appear here.
                    </td>
                  </tr>
                ) : (
                  rows.map(renderRow)
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Detail drawer */}
      <Sheet open={!!detailUser} onOpenChange={(o) => !o && closeDrawer()}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto bg-white"
          data-testid="pending-detail-drawer"
        >
          <SheetHeader className="mb-5">
            <SheetTitle className="font-display text-2xl text-[#05223D]">
              {detailUser?.name || 'Customer profile'}
            </SheetTitle>
            <SheetDescription className="text-[#465B70]">
              Review profile, add internal notes, then finalize.
            </SheetDescription>
          </SheetHeader>

          {detailsLoading && (
            <div className="py-12 flex items-center justify-center text-[#465B70]">
              <Loader2 className="w-6 h-6 animate-spin text-[#F25C05]" />
            </div>
          )}

          {details && (
            <div className="space-y-5">
              {/* Customer revisions request (if any) */}
              {details.user.revisions_request && (
                <div
                  className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm"
                  data-testid="revisions-request-card"
                >
                  <div className="flex items-center gap-2 mb-1 text-rose-800 font-bold">
                    <AlertCircle size={14} /> Customer requested revisions
                  </div>
                  <p className="text-rose-900">
                    {details.user.revisions_request.message}
                  </p>
                  <p className="text-[11px] text-rose-700 mt-2">
                    {details.user.revisions_request.at &&
                      new Date(details.user.revisions_request.at).toLocaleString()}
                  </p>
                </div>
              )}

              {/* Quick facts */}
              <div className="grid grid-cols-3 gap-2 text-sm">
                <Fact icon={<PawPrint size={14} />} label="Pets" value={details.counts.pets} />
                <Fact icon={<MapPin size={14} />} label="Addresses" value={details.counts.addresses} />
                <Fact icon={<Calendar size={14} />} label="Subscriptions" value={details.counts.subscriptions} />
              </div>

              {/* Account */}
              <div className="rounded-xl border border-[#0A4D8C1A] bg-[#FDFBF7] p-4">
                <h3 className="font-display font-bold text-[#05223D] mb-2">Account</h3>
                <KV k="Email" v={details.user.email} />
                <KV k="Phone" v={details.user.phone || <em className="text-rose-700">missing</em>} />
                <KV k="Provider" v={details.user.provider} />
                <KV k="Joined" v={details.user.created_at ? new Date(details.user.created_at).toLocaleString() : '—'} />
              </div>

              {/* Pets */}
              {details.pets.length > 0 && (
                <CollapsibleSection title={`Pets (${details.pets.length})`}>
                  {details.pets.map((p) => (
                    <div key={p.pet_id || p.id} className="rounded-lg border border-[#0A4D8C14] bg-white p-3 text-sm">
                      <div className="font-bold text-[#05223D]">{p.name || '—'} <span className="text-xs font-normal text-[#7A8A99] uppercase">· {p.species}</span></div>
                      <div className="grid grid-cols-2 gap-x-3 mt-1 text-xs text-[#465B70]">
                        {p.breed && <span>Breed: {p.breed}</span>}
                        {p.weight_kg && <span>Weight: {p.weight_kg} kg</span>}
                        {p.approx_age && <span>Age: {p.approx_age}</span>}
                      </div>
                      {p.dietary_notes && <p className="text-xs text-[#465B70] mt-1 italic">"{p.dietary_notes}"</p>}
                    </div>
                  ))}
                </CollapsibleSection>
              )}

              {/* Addresses */}
              {details.addresses.length > 0 && (
                <CollapsibleSection title={`Addresses (${details.addresses.length})`}>
                  {details.addresses.map((a) => (
                    <div key={a.id} className="rounded-lg border border-[#0A4D8C14] bg-white p-3 text-sm">
                      <div className="font-bold text-[#05223D]">{a.label || 'Address'}</div>
                      <div className="text-xs text-[#465B70]">
                        {[a.street, a.building && `bldg ${a.building}`, a.apartment && `apt ${a.apartment}`].filter(Boolean).join(', ')}
                      </div>
                      <div className="text-xs text-[#465B70]">{[a.district, a.city].filter(Boolean).join(', ')}</div>
                    </div>
                  ))}
                </CollapsibleSection>
              )}

              {/* Subscriptions */}
              {details.subscriptions.length > 0 && (
                <CollapsibleSection title={`Subscriptions (${details.subscriptions.length})`}>
                  {details.subscriptions.map((s) => (
                    <div key={s.id} className="rounded-lg border border-[#0A4D8C14] bg-white p-3 text-sm">
                      <div className="font-bold text-[#05223D]">{s.label || s.frequency} <span className="text-xs font-normal text-[#7A8A99]">· {s.frequency}</span></div>
                      {(s.items_hydrated || s.items || []).length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs">
                          {(s.items_hydrated || s.items).map((it, i) => (
                            <li key={i} className="flex items-center justify-between">
                              <span>{it.name || it.product_name || it.product_id || 'Item'}</span>
                              <span className="text-[#465B70]">×{it.qty || it.quantity || 1}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </CollapsibleSection>
              )}

              {/* Internal admin notes */}
              <div className="rounded-xl border border-[#F25C0540] bg-[#FFF8F1] p-4">
                <h3 className="flex items-center gap-2 font-display font-bold text-[#05223D] mb-2">
                  <StickyNote size={16} className="text-[#F25C05]" />
                  Internal notes (not visible to customer)
                </h3>
                <Textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={4}
                  placeholder="e.g. Called on WhatsApp — confirmed 2kg/week of Royal Canin..."
                  className="text-sm"
                  data-testid="admin-notes-textarea"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={onSaveNotes}
                    disabled={savingNotes}
                    data-testid="save-notes-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#F25C0526] hover:bg-[#F25C0540] text-[#F25C05] text-[11px] font-bold px-3 py-1.5"
                  >
                    {savingNotes && <Loader2 className="h-3 w-3 animate-spin" />}
                    Save notes
                  </button>
                </div>
              </div>

              {/* Action row */}
              <div className="flex flex-wrap gap-2 sticky bottom-0 bg-white pt-3 -mx-6 px-6 pb-2 border-t border-[#0A4D8C14]">
                {details.user.customer_status === 'pending_customer_confirmation' && (
                  <button
                    type="button"
                    onClick={onRevert}
                    disabled={reverting}
                    data-testid="revert-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#0A4D8C40] hover:bg-[#0A4D8C0F] text-[#0A4D8C] text-xs font-bold px-3 py-2"
                  >
                    {reverting && <Loader2 className="h-3 w-3 animate-spin" />}
                    <RotateCcw size={12} /> Send back to review
                  </button>
                )}
                {(details.user.customer_status === 'pending_admin_review' ||
                  details.user.customer_status === 'revisions_requested') && (
                  <button
                    type="button"
                    onClick={onFinalize}
                    disabled={finalizing}
                    data-testid="finalize-btn"
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#F25C05] hover:bg-[#D95204] disabled:opacity-60 text-white text-xs font-bold px-4 py-2 shadow-sm"
                  >
                    {finalizing ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <CheckCircle2 size={14} />
                    )}
                    Finalize &amp; send to customer
                  </button>
                )}
                {details.user.customer_status === 'pending_customer_confirmation' && (
                  <Badge className="ml-auto bg-blue-100 text-blue-800">
                    Waiting on customer
                  </Badge>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatCard({ label, count, icon, tone }) {
  const bg = {
    amber: 'bg-amber-50 border-amber-200',
    blue: 'bg-blue-50 border-blue-200',
    rose: 'bg-rose-50 border-rose-200',
  }[tone] || 'bg-white';
  return (
    <div className={`rounded-xl border ${bg} px-4 py-3`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#465B70]">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-[#05223D] mt-1 tabular-nums">{count}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="text-sm py-0.5">
      <span className="text-[#7A8A99]">{k}: </span>
      <span className="text-[#05223D] font-medium">{v ?? '—'}</span>
    </div>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-[#0A4D8C14] bg-white px-3 py-2 text-center">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-[#7A8A99]">
        {icon} {label}
      </div>
      <div className="text-xl font-bold text-[#05223D] tabular-nums">{value}</div>
    </div>
  );
}

function CollapsibleSection({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="rounded-xl border border-[#0A4D8C1A] bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#FDFBF7]"
      >
        <h3 className="font-display font-bold text-sm text-[#05223D]">{title}</h3>
        <span className="text-[#7A8A99] text-xs">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </section>
  );
}
